import { AccountInfo } from '@azure/msal-browser';
import { IEmployee } from '../../classes/IEmployee';
import { IUser } from '../../classes/IUser';

type AccountClaims = AccountInfo['idTokenClaims'];

export const getGidFromClaims = (claims: AccountClaims | undefined): string | null => {
    if (!claims) {
        return null;
    }

    const gidCandidates = [claims['gid'], claims['siemens_gid']];

    const gid = gidCandidates.find((value): value is string => typeof value === 'string' && value.length > 0);

    return gid ?? null;
};

export const mergeEmployeeWithClaims = (employee: IEmployee, account: AccountInfo, gid: string): IEmployee => {
    const mappedUser = mapClaimsToUser(account, gid);

    return {
        ...employee,
        gid: employee.gid ?? gid,
        user: {
            ...mappedUser,
            ...(employee.user ?? {})
        }
    };
};

const mapClaimsToUser = (account: AccountInfo, gid: string): IUser => {
    const claims = account.idTokenClaims ?? {};
    const firstName = getStringClaim(claims, ['given_name', 'preferred_given_name']);
    const surName = getStringClaim(claims, ['family_name', 'preferred_family_name']);
    const displayName = getStringClaim(claims, ['name']) ?? account.name ?? '';
    const department = getStringClaim(claims, ['department']) ?? '';
    const mail = getStringClaim(claims, ['email', 'preferred_username']) ?? account.username ?? '';

    return {
        country: getStringClaim(claims, ['country']) ?? '',
        department,
        departmentText: department,
        displayName,
        firstName,
        surName,
        gid,
        id: 0,
        initials: `${firstName.charAt(0)}${surName.charAt(0)}`.trim(),
        location: getStringClaim(claims, ['location']) ?? '',
        mail,
        mobile: getStringClaim(claims, ['mobile_phone']) ?? '',
        nickName: getStringClaim(claims, ['nickname', 'preferred_given_name']),
        preferredSN: getStringClaim(claims, ['preferred_family_name']),
        roles: getStringArrayClaim(claims, ['roles'])
    };
};

const getStringClaim = (claims: AccountClaims, claimNames: string[]): string | null => {
    for (const claimName of claimNames) {
        const value = claims?.[claimName];

        if (typeof value === 'string' && value.length > 0) {
            return value;
        }
    }

    return null;
};

const getStringArrayClaim = (claims: AccountClaims, claimNames: string[]): string[] => {
    for (const claimName of claimNames) {
        const value = claims?.[claimName];

        if (Array.isArray(value) && value.every((item) => typeof item === 'string')) {
            return value;
        }
    }

    return [];
};
